/* eslint-disable @next/next/no-html-link-for-pages */
/* eslint-disable @typescript-eslint/naming-convention */
import {NextPageContext} from 'next'
import {useMemo, useCallback, PropsWithChildren} from 'react'

import {IcNewNpayLogo, IcClose, FlexibleLayout, ButtonBox} from '@defign/ui'
import classNames from 'classnames/bind'

import CONFIG from '$config'
import {EXTERNAL_ROUTES} from '$constants/external-routes'
import baseOrigin from '$utils/baseOrigin'

import Styles from './_error.module.scss'

const cx = classNames.bind(Styles)

export enum ERROR_TYPE {
    STOP = 'STOP',
    NOT_FOUND = 'NOT_FOUND',
    CONNECT_FAILURE = 'CONNECT_FAILURE',
}

export const ERROR_TEMPLATE_INFO = {
    [ERROR_TYPE.STOP]: {
        title: '네이버페이 이용이 정지되었습니다.',
        description: (
            <>
                관련 내용은 고객센터(1588-3819)에 문의주시면
                <br /> 친절히 안내해 드리겠습니다.
            </>
        ),
        button: '고객센터 바로가기',
        handleClick() {
            const url = `${CONFIG.URL.HELP}/support/service/main.help?serviceNo=11714`
            window.location.href = url
        },
    },
    [ERROR_TYPE.NOT_FOUND]: {
        title: '페이지를 찾을 수 없습니다.',
        description: (
            <>
                잘못된 주소이거나 변경 혹은 삭제되어
                <br /> 요청하신 페이지를 찾을 수 없습니다.
            </>
        ),
        button: '이전 페이지로',
        handleClick() {
            window.history.back()
        },
    },
    [ERROR_TYPE.CONNECT_FAILURE]: {
        title: '네이버페이 서비스에 접속할 수 없습니다.',
        description: (
            <>
                일시적인 네트워크 오류로 서비스에
                <br /> 접속할 수 없습니다.
                <br /> 잠시 후 다시 시도해 주세요.
            </>
        ),
        button: '이전 페이지로',
        handleClick() {
            window.history.back()
        },
    },
}

function Error({
    type,
    statusCode,
}: PropsWithChildren<{
    type?: string
    statusCode?: number
}>) {
    const ERROR_TEMPLATE = useMemo(() => {
        const errorType =
            type ||
            (statusCode === 404
                ? ERROR_TYPE.NOT_FOUND
                : statusCode === 403
                  ? ERROR_TYPE.STOP
                  : ERROR_TYPE.CONNECT_FAILURE)

        if (typeof window !== 'undefined' && window.navigator.onLine === false) {
            return ERROR_TEMPLATE_INFO[ERROR_TYPE.CONNECT_FAILURE]
        } else {
            return ERROR_TEMPLATE_INFO[errorType as ERROR_TYPE]
        }
    }, [type, statusCode])

    const handleCloseClick = useCallback(() => {
        const history = global?.window?.history

        if (history.length === undefined || history.length < 2) {
            window.location.href = `${baseOrigin}${EXTERNAL_ROUTES.MYDATA.HOME}`
        } else {
            history.back()
        }
    }, [])

    return (
        <FlexibleLayout
            topElement={
                <header className={cx('header')}>
                    <div className={cx('inner')}>
                        <h1 className="blind">네이버페이</h1>
                        <div className={cx('aside-left')}>
                            <a href="/" className={cx('link-npay')}>
                                <IcNewNpayLogo width={66} height={22} />
                                <span className="blind">네이버페이</span>
                            </a>
                        </div>
                        <div className={cx('aside-right')}>
                            <button type="button" className={cx('button-close')} onClick={handleCloseClick}>
                                <IcClose width={24} height={24} />
                                <span className="blind">화면닫기</span>
                            </button>
                        </div>
                    </div>
                </header>
            }
            isFullHeight
            colorType="gray100"
            contentColorType="white"
        >
            <div className={cx('article')}>
                <h2 className={cx('title')}>{ERROR_TEMPLATE.title}</h2>
                <p className={cx('description')}>{ERROR_TEMPLATE.description}</p>
                <div className={cx('button-area')}>
                    <ButtonBox
                        size="medium"
                        text={ERROR_TEMPLATE.button}
                        variant="tertiary"
                        onClick={ERROR_TEMPLATE.handleClick}
                    />
                </div>
            </div>
        </FlexibleLayout>
    )
}

Error.getInitialProps = ({res, err}: NextPageContext) => {
    const statusCode = res ? res.statusCode : err ? err.statusCode : 404

    if (res) {
        const userAgent = res.req.headers['user-agent'] || ''

        return {statusCode, userAgent}
    } else {
        const userAgent = window.navigator.userAgent

        return {statusCode, userAgent}
    }
}

export default Error
